import React, { useState, useEffect, useCallback, Fragment, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Chip, IconButton, Button,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, CircularProgress, Alert,
} from '@mui/material';
import {
  Folder as ProjectIcon,
  Star as TargetIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  CloudDownload as DownloadIcon,
  CloudUpload as PushIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { formatInTimeZone } from 'date-fns-tz';
import { useFileWebSocket } from '../../hooks/useFileWebSocket';
import { useObservationSelection } from '../../hooks/useObservationSelection';
import SelectionBar from './SelectionBar';
import FileGrid from './FileGrid';
import { computeGroupStats, sumFileBytes } from '../../utils/fileStats';
import { formatSize } from '../../utils/fileUtils';
import { getAccessToken } from '../../services/authStorage';

const fileIdsOf = (files) => (files || []).map((f) => f._id || f.file_id); // server bulk-delete matches _id

const API_BASE_URL = '/api/v1';

const nestedAccordionSx = {
  '&:before': { display: 'none' },
  boxShadow: 'none',
  borderTop: '1px solid',
  borderColor: 'divider',
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    return formatInTimeZone(new Date(date), 'UTC', 'yyyy-MM-dd HH:mm') + ' UTC';
  } catch { return 'N/A'; }
};

const groupFilesByTarget = (files) => {
  const byTarget = {};
  (files || []).forEach(f => {
    const t = f.target_name || 'Unknown Target';
    if (!byTarget[t]) byTarget[t] = [];
    byTarget[t].push(f);
  });
  return byTarget;
};

const groupFilesByObservation = (files) => {
  const byObs = {};
  (files || []).forEach(f => {
    const obsId = f.observation_id?.toString() || 'unknown';
    if (!byObs[obsId]) byObs[obsId] = { name: f.observation_name || obsId, files: [] };
    byObs[obsId].files.push(f);
  });
  return byObs;
};

const ProjectGroupList = ({ searchTerm, onDownloadGroup, onPushGroup, onPreview, getDownloadUrl, getCalibratedDownloadUrl, downloadCalibrated, onCalibrateError, onStatsLoaded, onDelete, canDelete, onBulkDelete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedTargets, setExpandedTargets] = useState(new Set());
  const [expandedObservations, setExpandedObservations] = useState(new Set());
  const [loadedFiles, setLoadedFiles] = useState({});
  const [masterCoverage, setMasterCoverage] = useState({});
  const [loadingFiles, setLoadingFiles] = useState(new Set());
  const loadedFilesRef = useRef(loadedFiles);
  loadedFilesRef.current = loadedFiles;
  const selectionEnabled = !!onBulkDelete;
  const selection = useObservationSelection();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/user-files/by-project`, {
        headers: { Authorization: `Bearer ${token}` },
        params: searchTerm ? { search: searchTerm } : {}
      });
      if (response.data.success) {
        setData(response.data.data);
        setLoadedFiles({});
        if (onStatsLoaded && response.data.data?.groups) {
          onStatsLoaded(computeGroupStats(response.data.data.groups, { observations: response.data.data.total_observations }));
        }
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, onStatsLoaded]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchGroupFiles = useCallback(async (projectId) => {
    if (loadedFilesRef.current[projectId]) return;
    setLoadingFiles(prev => new Set(prev).add(projectId));
    try {
      const token = getAccessToken();
      const params = { groupType: 'project', groupId: projectId };
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get(`${API_BASE_URL}/user-files/group-files`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      if (response.data.success) {
        setLoadedFiles(prev => ({ ...prev, [projectId]: response.data.data.files }));
        if (response.data.data.master_coverage) {
          setMasterCoverage(prev => ({ ...prev, ...response.data.data.master_coverage }));
        }
      }
    } catch (err) {
      console.error('Failed to load files for project:', projectId, err);
    } finally {
      setLoadingFiles(prev => {
        const s = new Set(prev);
        s.delete(projectId);
        return s;
      });
    }
  }, [searchTerm]);

  const handleFileUpdate = useCallback((update) => {
    setLoadedFiles(prev => {
      // Object-wrap the mutation flag so the map() callback below doesn't
      // trip eslint no-loop-func (closing over a let in a for loop).
      const flag = { updated: false };
      const next = {};
      for (const [key, files] of Object.entries(prev)) {
        const updatedFiles = files.map(file => {
          const fileId = file.file_id || file._id;
          if (fileId === update.file_id) {
            flag.updated = true;
            return { ...file, plate_solve_status: update.plate_solve_status, plate_solve_result: update.plate_solve_result || file.plate_solve_result };
          }
          return file;
        });
        next[key] = updatedFiles;
      }
      return flag.updated ? next : prev;
    });
  }, []);

  useFileWebSocket(handleFileUpdate);

  const handleSolveFile = async (fileId) => {
    try {
      const token = getAccessToken();
      await axios.post(`${API_BASE_URL}/user-files/${fileId}/plate-solve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { alert('Failed to queue plate solving job'); }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const s = new Set(prev);
      if (s.has(projectId)) {
        s.delete(projectId);
      } else {
        s.add(projectId);
        fetchGroupFiles(projectId);
      }
      return s;
    });
  };

  const toggleSet = (setter, key) => {
    setter(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data?.groups?.length) {
    return <Typography color="textSecondary" sx={{ textAlign: 'center', p: 3 }}>No projects with files found</Typography>;
  }

  return (
    <Stack spacing={2}>
      {selectionEnabled && (
        <SelectionBar
          count={selection.size}
          onClear={selection.clear}
          onDelete={() => onBulkDelete(selection.getSelectedFileIds())}
        />
      )}
      {data.groups.map((group) => {
        const groupId = group._id || group.project_name;
        const files = loadedFiles[groupId];
        const isLoadingFiles = loadingFiles.has(groupId);
        const targetGroups = files ? groupFilesByTarget(files) : {};

        return (
          <Accordion
            key={groupId}
            expanded={expandedProjects.has(groupId)}
            onChange={() => toggleProject(groupId)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                <ProjectIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6">{group.project_name}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={`${group.target_count || 0} targets`} size="small" />
                  <Chip label={`${group.total_files} files`} size="small" />
                  <Chip label={`${group.observation_count} obs`} size="small" />
                  <Chip label={formatSize(group.total_size)} size="small" variant="outlined" />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => { e.stopPropagation(); onDownloadGroup('project', group._id, group.project_name); }}
                    title={`Download all files for ${group.project_name}`}
                  >
                    <DownloadIcon />
                  </IconButton>
                  {onPushGroup && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); onPushGroup('project', group._id, group.project_name); }}
                      title={`Push all files for ${group.project_name} to external storage`}
                    >
                      <PushIcon />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {isLoadingFiles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !files ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} align="center">
                  No files in this project
                </Typography>
              ) : Object.keys(targetGroups).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} align="center">
                  No files in this project
                </Typography>
              ) : (
                <Box>
                  {Object.entries(targetGroups).sort(([a], [b]) => a.localeCompare(b)).map(([targetName, targetFiles]) => {
                    const targetKey = `${groupId}-${targetName}`;
                    const targetSolved = targetFiles.filter(f => f.plate_solve_result?.solved === true).length;
                    const obsGroups = groupFilesByObservation(targetFiles);
                    // Ordered, selectable observation rows for this target (range
                    // scope is confined to this target's table). A row is
                    // selectable only if the user may delete all of its files.
                    const obsEntries = Object.entries(obsGroups);
                    const selectableObs = obsEntries.filter(([, obsData]) =>
                      !canDelete || obsData.files.every((f) => canDelete(f)));
                    const orderedKeys = selectableObs.map(([obsId]) => `${targetKey}-${obsId}`);
                    const selectableIndex = new Map(orderedKeys.map((k, i) => [k, i]));
                    const fileIdsByKey = Object.fromEntries(
                      selectableObs.map(([obsId, obsData]) => [`${targetKey}-${obsId}`, fileIdsOf(obsData.files)]));

                    return (
                      <Accordion
                        key={targetKey}
                        expanded={expandedTargets.has(targetKey)}
                        onChange={() => toggleSet(setExpandedTargets, targetKey)}
                        disableGutters
                        sx={nestedAccordionSx}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pl: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                              <TargetIcon sx={{ mr: 1, fontSize: 20 }} color="action" />
                              <Typography variant="subtitle1">{targetName}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Chip label={`${targetFiles.length} files`} size="small" />
                              <Chip label={formatSize(sumFileBytes(targetFiles))} size="small" variant="outlined" />
                              {targetSolved > 0 && (
                                <Chip label={`${targetSolved} solved`} size="small" color="success" variant="outlined" />
                              )}
                            </Stack>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0, pl: 4 }}>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell width={30}></TableCell>
                                  <TableCell>Observation</TableCell>
                                  <TableCell>Files</TableCell>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {obsEntries.map(([obsId, obsData]) => {
                                  const obsKey = `${targetKey}-${obsId}`;
                                  const selectable = selectionEnabled && selectableIndex.has(obsKey);
                                  return (
                                    <Fragment key={obsId}>
                                      <TableRow
                                        hover
                                        selected={selectable && selection.isSelected(obsKey)}
                                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                                        title={selectable ? 'Click to expand · Shift/Ctrl-click to select' : undefined}
                                        // Shift/Ctrl/Cmd-click selects the row; a
                                        // plain click expands it as usual.
                                        onClick={(e) => {
                                          if (selectable && (e.shiftKey || e.ctrlKey || e.metaKey)) {
                                            selection.handleClick(targetKey, selectableIndex.get(obsKey), orderedKeys, fileIdsByKey, e);
                                          } else {
                                            toggleSet(setExpandedObservations, obsKey);
                                          }
                                        }}
                                      >
                                        <TableCell>
                                          <IconButton size="small" sx={{ p: 0 }}>
                                            {expandedObservations.has(obsKey) ?
                                              <KeyboardArrowDown fontSize="small" /> :
                                              <KeyboardArrowRight fontSize="small" />
                                            }
                                          </IconButton>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">{obsData.name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip label={`${obsData.files.length}`} size="small" />
                                            <Typography variant="caption" color="text.secondary">
                                              {formatSize(sumFileBytes(obsData.files))}
                                            </Typography>
                                          </Stack>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">{formatDate(obsData.files[0]?.uploaded_at)}</Typography>
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => onDownloadGroup('observation', obsId, obsData.name)}
                                            title="Download"
                                          >
                                            <DownloadIcon fontSize="small" />
                                          </IconButton>
                                          {onPushGroup && (
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              onClick={() => onPushGroup('observation', obsId, obsData.name)}
                                              title="Push externally"
                                            >
                                              <PushIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                                          <Collapse in={expandedObservations.has(obsKey)} timeout="auto" unmountOnExit>
                                            <Box sx={{ pl: 4, pr: 2, py: 1, bgcolor: 'action.hover' }}>
                                              <FileGrid
                                                files={obsData.files}
                                                onPreview={onPreview}
                                                onSolve={handleSolveFile}
                                                getDownloadUrl={getDownloadUrl}
                                                getCalibratedDownloadUrl={getCalibratedDownloadUrl}
                                                downloadCalibrated={downloadCalibrated}
                                                onCalibrateError={onCalibrateError}
                                                masterCoverage={masterCoverage}
                                                onDelete={onDelete}
                                                canDelete={canDelete}
                                              />
                                            </Box>
                                          </Collapse>
                                        </TableCell>
                                      </TableRow>
                                    </Fragment>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                  {group.total_files > (files?.length || 0) && (
                    <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button
                        size="small"
                        onClick={() => navigate(`/files/project/${group._id}`)}
                      >
                        Showing {files?.length} of {group.total_files} &mdash; View All &rarr;
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default ProjectGroupList;
